import { com } from '@eidu/form'
import React, { useState } from 'react'
import { Button, Card, Stack } from '@mui/material'
import EntityTypeField from '../../pages/permission/path/EntityTypeField'
import { RenderablePath } from '../../pages/permission/path/PathField'
import createSemanticLink from '../../api/analytics/createSemanticLink'
import AuthenticationContext from '../../api/authorization/AuthenticationContext'
import { useSnackBar } from '../feedback/SnackBarProvider'
import ComplexPathField from './ComplexPathField'
import useRenderablePaths from '../misc/useRenderablePaths'
import SemanticLinkKeyConfiguration = com.eidu.sharedlib.entity.analytics.SemanticLinkKeyConfiguration
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import SemanticLink = com.eidu.sharedlib.entity.analytics.SemanticLink
import EntityPath = com.eidu.sharedlib.entity.path.EntityPath
import semanticLinkIdFromString = com.eidu.sharedlib.entity.analytics.semanticLinkIdFromString

type SemanticLinkFieldProps = {
  entityType: EntityTypeId
  entityTypes: readonly EntityType[]
  semanticLinkKeyConfiguration: SemanticLinkKeyConfiguration
  authContext: AuthenticationContext
  onSave: () => void
  onCancel: () => void
}

const SemanticLinkField = ({
  entityType,
  entityTypes,
  semanticLinkKeyConfiguration,
  authContext,
  onSave,
  onCancel,
}: SemanticLinkFieldProps) => {
  const availableTargetEntityTypes = entityTypes.filter(({ kind }) =>
    semanticLinkKeyConfiguration.toTypeKind ? kind === semanticLinkKeyConfiguration.toTypeKind : true
  )
  const [selectedTargetTypeId, setSelectedTargetTypeId] = useState(availableTargetEntityTypes.at(0)?.id)
  const [selectedPath, setSelectedPath] = useState<EntityPath>()
  const [isSaving, setIsSaving] = useState(false)
  const { showSnackBar } = useSnackBar()

  const targetType =
    selectedTargetTypeId && entityTypes.find(({ id: entityTypeId }) => entityTypeId === selectedTargetTypeId)

  const renderablePaths: RenderablePath[] = useRenderablePaths(entityType, targetType, entityTypes)

  const handleSave = async () => {
    setIsSaving(true)

    try {
      if (selectedPath) {
        const newId = semanticLinkIdFromString(crypto.randomUUID())
        const newSemanticLink = new SemanticLink(newId, semanticLinkKeyConfiguration.key, selectedPath)

        await createSemanticLink({ authContext, semanticLink: newSemanticLink })
          .then(() => onSave())
          .then(() => showSnackBar('Successfully created semantic link', 'success'))
      } else {
        showSnackBar(`Error creating semantic link: invalid path`, 'error')
      }
    } catch (error) {
      showSnackBar(`Error creating semantic link: ${error instanceof Error ? error.message : String(error)}`, 'error')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Card>
      <Stack padding={2} spacing={2}>
        <EntityTypeField
          entityTypeId={selectedTargetTypeId}
          onChange={(type) => {
            setSelectedTargetTypeId(type.id)
            setSelectedPath(undefined)
          }}
          types={availableTargetEntityTypes}
          label="Target type"
        />
        <ComplexPathField
          entityTypeId={entityType}
          targetType={targetType}
          selectedPath={selectedPath}
          setSelectedPath={setSelectedPath}
          renderablePaths={renderablePaths}
          semanticLinkKeyConfiguration={semanticLinkKeyConfiguration}
        />
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={isSaving || !selectedPath}>
            Save
          </Button>
        </Stack>
      </Stack>
    </Card>
  )
}

export default SemanticLinkField
